'use client';

import { useSyncExternalStore } from 'react';

/** Why the last checkout attempt did not reach the hold. The cart footer
 * shows it with a one-click way back to shipping. */
export type PickupNotice = {
  kind: 'multi' | 'unavailable' | 'unreachable' | 'rate_limited' | 'error';
  /** Server copy for `unavailable` (a 422); other kinds use catalog copy. */
  message?: string;
};

let notice: PickupNotice | null = null;
const listeners = new Set<() => void>();

export const setPickupNotice = (next: PickupNotice | null): void => {
  if (notice === next) return;
  notice = next;
  listeners.forEach((fn) => fn());
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const usePickupNotice = (): PickupNotice | null =>
  useSyncExternalStore(
    subscribe,
    () => notice,
    () => null,
  );

/** Confirmation after a switch to shipping that added the PRO Charger back.
 * `signature` is the cart's lines at that moment (cartLinesSignature): the
 * message stays until the cart changes or checkout starts. */
export type ShipSwitchedNotice = {
  chargerCase: 'GOLD' | 'BLACK';
  signature: string;
};

let shipSwitched: ShipSwitchedNotice | null = null;
const shipSwitchedListeners = new Set<() => void>();

export const getShipSwitchedNotice = (): ShipSwitchedNotice | null => shipSwitched;

export const setShipSwitchedNotice = (next: ShipSwitchedNotice | null): void => {
  if (shipSwitched === next) return;
  shipSwitched = next;
  shipSwitchedListeners.forEach((fn) => fn());
};

export const useShipSwitchedNotice = (): ShipSwitchedNotice | null =>
  useSyncExternalStore(
    (fn) => {
      shipSwitchedListeners.add(fn);
      return () => {
        shipSwitchedListeners.delete(fn);
      };
    },
    () => shipSwitched,
    () => null,
  );

/** After checkout took other items out of a pickup cart (one ring, plus its
 * MAX and funnel add-ons, is the whole order): how many lines left.
 * `signature` is the cart's lines right after (cartLinesSignature): the
 * message stays until the cart changes again or checkout starts. */
export type PrunedNotice = {
  count: number;
  signature: string;
};

let pruned: PrunedNotice | null = null;
const prunedListeners = new Set<() => void>();

export const getPrunedNotice = (): PrunedNotice | null => pruned;

export const setPrunedNotice = (next: PrunedNotice | null): void => {
  if (pruned === next) return;
  pruned = next;
  prunedListeners.forEach((fn) => fn());
};

export const usePrunedNotice = (): PrunedNotice | null =>
  useSyncExternalStore(
    (fn) => {
      prunedListeners.add(fn);
      return () => {
        prunedListeners.delete(fn);
      };
    },
    () => pruned,
    () => null,
  );

/** True while a pickup checkout or a switch to shipping is running, so the
 * two cannot interleave. */
let busy = false;
const busyListeners = new Set<() => void>();

export const isPickupBusy = (): boolean => busy;

export const setPickupBusy = (next: boolean): void => {
  if (busy === next) return;
  busy = next;
  busyListeners.forEach((fn) => fn());
};

export const usePickupBusy = (): boolean =>
  useSyncExternalStore(
    (fn) => {
      busyListeners.add(fn);
      return () => {
        busyListeners.delete(fn);
      };
    },
    () => busy,
    () => false,
  );

/** Cart-level actions registered by CartProvider (which owns cart state), so
 * the footer notice and the cart row can call them without prop drilling. */
type PickupCartActions = { switchToShipping: () => Promise<void> };

let actions: PickupCartActions | null = null;

export const registerPickupCartActions = (
  next: PickupCartActions | null,
): void => {
  actions = next;
};

export const switchPickupToShipping = (): Promise<void> =>
  actions?.switchToShipping() ?? Promise.resolve();
